import React, { useState, useEffect, useRef } from 'react';
import axios from '../services/api';
import { Lock, Activity, RefreshCw, Crosshair, FileText, Settings, Code } from 'lucide-react';
import { Card } from '../components/ds/Card';
import { Button } from '../components/ds/Button';

const API_BASE = "/api";

export default function AdminPage() {
  const [msg, setMsg] = useState('');
  const [importType, setImportType] = useState('auto');
  const [importing, setImporting] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [testMatrixResult, setTestMatrixResult] = useState(null);
  const [loadingTest, setLoadingTest] = useState(false);
  const [testParams, setTestParams] = useState({ category: 'Driver', dexterity: 'RH', club_speed: 100, ball_speed: 150, launch_angle: 12, spin_rate: 2500 });
  const [debugMode, setDebugMode] = useState(localStorage.getItem('pf_debug') === 'true');
  const fileInputRef = useRef(null);

  const categorizeShafts = async () => {
    try {
      const res = await axios.post(`/admin/categorize-shafts`);
      setMsg(`Categorized: ${res.data.updated.iron} iron, ${res.data.updated.driver} driver`);
    } catch (e) { setMsg('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  const runMigrations = async () => {
    try {
      setMsg('Running migrations...');
      const res = await axios.post(`/admin/migrate`);
      setMsg(res.data.message || 'Migrations complete!');
    } catch (e) { setMsg('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  const seedDatabase = async () => {
    try {
      setMsg('Seeding database from seed files...');
      const res = await axios.post(`/admin/seed`);
      setMsg(res.data.message || 'Seeding complete!');
    } catch (e) { setMsg('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setMsg('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`/inventory/import/file?item_type=${importType}`, formData);
      setMsg(`${res.data.message} (${res.data.item_type})`);
    } catch (e) {
      setMsg('Import Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchHealth = async () => {
    setLoadingHealth(true);
    try {
      const [health, inventory] = await Promise.all([
        axios.get(`/health`),
        axios.get(`/health/inventory`)
      ]);
      setHealthData({ ...health.data, inventory: inventory.data });
    } catch (e) {
      setHealthData({ error: e.message });
    }
    setLoadingHealth(false);
  };

  const runTestMatrix = async () => {
    setLoadingTest(true);
    setTestMatrixResult(null);
    try {
      const params = new URLSearchParams(testParams).toString();
      const res = await axios.get(`/health/test-matrix?${params}`);
      setTestMatrixResult(res.data);
    } catch (e) {
      setTestMatrixResult({ error: e.response?.data?.detail || e.message });
    }
    setLoadingTest(false);
  };

  const clearAllHistory = async () => {
    if (!confirm('Delete ALL session history? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`/history/all`);
      setMsg(res.data?.message || 'History cleared');
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const toggleDebugMode = () => {
    const newVal = !debugMode;
    setDebugMode(newVal);
    localStorage.setItem('pf_debug', newVal.toString());
    setMsg(newVal ? 'Debug mode ON - console logs enabled' : 'Debug mode OFF');
  };

  useEffect(() => { fetchHealth(); }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h2 className="text-xl font-bold flex gap-2 items-center"><Lock size={18} className="text-red-500" /> Admin Panel</h2>
      {msg && <div className={`${msg.startsWith('Error') || msg.startsWith('Import Error') ? 'bg-red-900/30 text-red-300' : 'bg-emerald-900/30 text-emerald-300'} p-3 rounded text-sm`}>{msg}</div>}

      {/* Health Status Panel */}
      <Card padding="p-4" className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white flex items-center gap-2"><Activity size={16} className="text-success" /> System Health</h3>
          <Button variant="ghost" size="sm" onClick={fetchHealth} disabled={loadingHealth}>
            <RefreshCw size={14} className={loadingHealth ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>
        {healthData ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-background p-3 rounded-lg">
              <div className="text-text-muted">Status</div>
              <div className={`font-bold ${healthData.status === 'ok' ? 'text-success' : 'text-error'}`}>{healthData.status?.toUpperCase() || 'UNKNOWN'}</div>
            </div>
            <div className="bg-background p-3 rounded-lg">
              <div className="text-text-muted">Total Heads</div>
              <div className="font-bold text-white">{healthData.inventory?.totals?.heads || 0}</div>
            </div>
            <div className="bg-background p-3 rounded-lg">
              <div className="text-text-muted">Total Shafts</div>
              <div className="font-bold text-white">{healthData.inventory?.totals?.shafts || 0}</div>
            </div>
            <div className="bg-background p-3 rounded-lg">
              <div className="text-text-muted">OCR Service</div>
              <div className={`font-bold ${healthData.services?.ocr ? 'text-success' : 'text-error'}`}>{healthData.services?.ocr ? 'OK' : 'OFF'}</div>
            </div>
          </div>
        ) : loadingHealth ? (
          <div className="text-text-muted text-sm">Loading...</div>
        ) : null}

        {/* Inventory Breakdown */}
        {healthData?.inventory?.heads && (
          <div className="pt-3 border-t border-subtle">
            <div className="text-xs text-text-muted mb-2">Inventory by Category</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(healthData.inventory.heads).map(([key, count]) => (
                <span key={key} className="bg-surface-light px-2 py-1 rounded text-xs">
                  <span className="text-text-secondary">{key}:</span> <span className="text-white font-mono">{count}</span>
                </span>
              ))}
            </div>
            {healthData.inventory?.shafts && (
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(healthData.inventory.shafts).map(([cat, count]) => (
                  <span key={cat} className="bg-surface-light px-2 py-1 rounded text-xs">
                    <span className="text-text-secondary">{cat} Shafts:</span> <span className="text-white font-mono">{count}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {healthData?.inventory?.warning && <div className="text-warning text-xs mt-2">{healthData.inventory.warning}</div>}
        {healthData?.inventory?.error && <div className="text-error text-xs mt-2">{healthData.inventory.error}</div>}
      </Card>

      {/* Test Matrix Panel */}
      <Card padding="p-4" className="space-y-3">
        <h3 className="font-bold text-white flex items-center gap-2"><Crosshair size={16} className="text-primary" /> Test Matrix Recommendations</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <div>
            <label className="text-[10px] text-text-muted">Category</label>
            <select value={testParams.category} onChange={e => setTestParams({...testParams, category: e.target.value})} className="w-full bg-background border border-border p-1.5 rounded text-white text-xs">
              {['Driver', 'Fairway', 'Hybrid', 'Iron', 'Wedge'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-text-muted">Dexterity</label>
            <select value={testParams.dexterity} onChange={e => setTestParams({...testParams, dexterity: e.target.value})} className="w-full bg-background border border-border p-1.5 rounded text-white text-xs">
              <option value="RH">RH</option>
              <option value="LH">LH</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-text-muted">Club Spd</label>
            <input type="number" value={testParams.club_speed} onChange={e => setTestParams({...testParams, club_speed: +e.target.value})} className="w-full bg-background border border-border p-1.5 rounded text-white text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-text-muted">Ball Spd</label>
            <input type="number" value={testParams.ball_speed} onChange={e => setTestParams({...testParams, ball_speed: +e.target.value})} className="w-full bg-background border border-border p-1.5 rounded text-white text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-text-muted">Launch</label>
            <input type="number" value={testParams.launch_angle} onChange={e => setTestParams({...testParams, launch_angle: +e.target.value})} className="w-full bg-background border border-border p-1.5 rounded text-white text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-text-muted">Spin</label>
            <input type="number" value={testParams.spin_rate} onChange={e => setTestParams({...testParams, spin_rate: +e.target.value})} className="w-full bg-background border border-border p-1.5 rounded text-white text-xs" />
          </div>
        </div>
        <Button variant="primary" size="sm" onClick={runTestMatrix} disabled={loadingTest}>
          {loadingTest ? <><RefreshCw size={14} className="animate-spin" /> Testing...</> : 'Run Test'}
        </Button>
        {testMatrixResult && (
          <div className="bg-background p-3 rounded text-xs font-mono max-h-48 overflow-auto">
            {testMatrixResult.error ? (
              <span className="text-error">{testMatrixResult.error}</span>
            ) : (
              <>
                <div className="text-success mb-2">Found {testMatrixResult.heads_count} heads, {testMatrixResult.shafts_count} shafts</div>
                <div className="text-text-secondary">Heads: {testMatrixResult.heads?.map(h => h.name).join(', ') || 'None'}</div>
                <div className="text-text-secondary mt-1">Shafts: {testMatrixResult.shafts?.map(s => s.name).join(', ') || 'None'}</div>
                {testMatrixResult.baseline && <div className="text-text-muted mt-1">Baseline Carry: {testMatrixResult.baseline.carry}yd</div>}
              </>
            )}
          </div>
        )}
      </Card>

      {/* Bulk Import Section */}
      <Card padding="p-4" className="space-y-3">
        <h3 className="font-bold text-white flex items-center gap-2"><FileText size={16} className="text-warning" /> Bulk Import Inventory</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-text-muted block mb-1">Item Type</label>
            <select value={importType} onChange={e => setImportType(e.target.value)} className="w-full bg-background border border-border p-2 rounded text-white text-sm">
              <option value="auto">Auto-Detect</option>
              <option value="heads">Club Heads</option>
              <option value="shafts">Shafts</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-text-muted block mb-1">File</label>
            <input type="file" ref={fileInputRef} accept=".csv,.json,.xlsx,.xls" onChange={handleFileImport} disabled={importing}
              className="w-full bg-background border border-border p-1.5 rounded text-white text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-yellow-600 file:text-white hover:file:bg-yellow-500" />
          </div>
        </div>
        {importing && <div className="flex items-center gap-2 text-yellow-400 text-sm"><RefreshCw className="animate-spin" size={14} /> Importing...</div>}
        <div className="text-xs text-text-muted pt-2 border-t border-subtle">
          <p><strong>Heads:</strong> manufacturer, model_name, category, head_profile, loft, dexterity</p>
          <p><strong>Shafts:</strong> manufacturer, model_name, flex, weight_g, launch_profile, category</p>
        </div>
      </Card>

      {/* Admin Tools */}
      <Card padding="p-4" className="space-y-3">
        <h3 className="font-bold text-white flex items-center gap-2"><Settings size={16} /> Admin Tools</h3>
        <div className="flex gap-2 flex-wrap">
          <Button variant="success" size="sm" onClick={seedDatabase}>Seed Database</Button>
          <Button variant="warning" size="sm" onClick={runMigrations}>Run Migrations</Button>
          <Button variant="secondary" size="sm" onClick={categorizeShafts}>Auto-Categorize Shafts</Button>
          <Button variant="danger" size="sm" onClick={clearAllHistory}>Clear All History</Button>
        </div>
      </Card>

      {/* Real-Time Debugging Panel */}
      <Card padding="p-4" className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Activity size={16} className="text-purple-400" /> Real-Time Debugging
          </h3>
          <div className="text-xs text-text-muted">
            For live logs, use: <code className="text-text-secondary">gcloud run services logs read pocketfitter --region us-central1 --project concise-slate-484820-c1</code>
          </div>
        </div>

        {/* CSV/OCR Test Section */}
        <div className="bg-background p-3 rounded space-y-2">
          <div className="text-sm font-medium text-text-secondary">Quick Upload Test</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-text-muted block mb-1">Test CSV Upload</label>
              <input type="file" accept=".csv" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  setMsg('Testing CSV upload...');
                  const res = await axios.post('/analyze/upload?category=Driver&dexterity=RH', formData);
                  setMsg(`✅ CSV Success: ${res.data.shot_count || 0} shots, ${res.data.heads?.length || 0} head recommendations`);
                } catch (e) {
                  setMsg(`❌ CSV Error: ${e.response?.data?.detail || e.message}`);
                }
              }} className="w-full text-xs bg-surface border border-border p-2 rounded text-white file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-blue-600 file:text-white hover:file:bg-blue-500" />
            </div>
            <div>
              <label className="text-xs text-text-muted block mb-1">Test OCR Upload</label>
              <input type="file" accept="image/*" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  setMsg('Testing OCR upload...');
                  const res = await axios.post('/analyze/ocr?category=Driver&dexterity=RH', formData);
                  setMsg(`✅ OCR Success: Extracted ${Object.keys(res.data.aggregated || {}).length} fields`);
                } catch (e) {
                  setMsg(`❌ OCR Error: ${e.response?.data?.detail || e.message}`);
                }
              }} className="w-full text-xs bg-surface border border-border p-2 rounded text-white file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-purple-600 file:text-white hover:file:bg-purple-500" />
            </div>
          </div>
        </div>

        {/* Common Error Patterns */}
        <div className="bg-background p-3 rounded">
          <div className="text-sm font-medium text-text-secondary mb-2">Common Issues & Fixes</div>
          <div className="space-y-1 text-xs text-text-secondary">
            <div>• <strong className="text-text-secondary">CSV timeout:</strong> Check file size (&lt;5MB) and format validity</div>
            <div>• <strong className="text-text-secondary">OCR error:</strong> Verify Gemini API key is active at <code className="text-blue-400">aistudio.google.com</code></div>
            <div>• <strong className="text-text-secondary">Missing data:</strong> Ensure CSV has required fields: ball_speed, launch_angle, spin_rate</div>
            <div>• <strong className="text-text-secondary">Empty recommendations:</strong> Check inventory counts above - may need to seed database</div>
          </div>
        </div>
      </Card>

      {/* Debug Toggles */}
      <Card padding="p-4" className="space-y-3">
        <h3 className="font-bold text-white flex items-center gap-2"><Code size={16} className="text-purple-400" /> Debug Options</h3>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={debugMode} onChange={toggleDebugMode} className="w-4 h-4 rounded bg-surface-light border-border" />
            <span className="text-sm text-text-secondary">Debug Mode (verbose console logs)</span>
          </label>
        </div>
        <div className="text-xs text-text-muted pt-2 border-t border-subtle">
          <p><strong>API Base:</strong> <code className="text-text-secondary">{API_BASE}</code></p>
          <p><strong>Endpoints:</strong> /health, /health/inventory, /health/test-matrix, /admin/logs</p>
        </div>
      </Card>
    </div>
  );
}
